import { useFpB2bCalculateResultWithTransactionInfo } from '@cart-services/hooks/fp-b2b/use-fp-b2b-calculate-result-with-transaction-info'
import { useFpB2bCartCalculate } from '@cart-services/hooks/fp-b2b/use-fp-b2b-cart-calculate'
import { useFpB2bDeliveryClassList } from '@cart-services/hooks/fp-b2b/use-fp-b2b-delivery-class-list'
import { useFpB2bMerchantService } from '@cart-services/hooks/fp-b2b/use-fp-b2b-merchant-service'
import { useFpB2bPaymentService } from '@cart-services/hooks/fp-b2b/use-fp-b2b-payment-service'
import { useFpB2bPaymentTransactionPolling } from '@cart-services/hooks/fp-b2b/use-fp-b2b-payment-transaction-polling'
import { LoaderFullPage } from '@cart-ui/LoaderFullPage/LoaderFullPage'
import { type Invoice } from '@constants'
import { datadogRum } from '@datadog/browser-rum'
import { DialogPayMeQRCode } from '@dialog-qr-code/DialogPayMeQRCode'
import { useDialog } from '@dni-hooks/useDialog'
import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { useApiPaymentTransaction } from '@hooks-api/eCommerceV2/useApiPaymentTransaction'
import { useApiCampaignMember } from '@hooks-api/fortune/useApiCampaignMember'
import { useApiGetMemberPaymentTransactionRequest } from '@hooks-api/fortune/useApiGetMemberPaymentTransactionRequest'
import { useApiGetPaymentTransactionMutation } from '@hooks-api/fortune/useApiGetPaymentTransactionMutation'
import { useApiMerchantProfile } from '@hooks-api/fortune-fastpass/useApiMerchantProfile'
import { FastpassOrderStatus } from '@hooks-api/types/dataModel/fpOrder'
import { type CheckoutOrder } from '@hooks-api/types/dataModel/orders'
import { DELIVERY_CLASS } from '@hooks-api/types/dataModel/shipping'
import { getCkoPaymentErrorInfo, CheckoutPayAlipayHkModule, CheckoutPayApplePayModule, CheckoutPayCreditCardModule } from '@payments'
import { isPayMeUrl } from '@payments/lib/is-payme-url'
import { PaymentError } from '@payments/lib/PaymentError'
import { Alert } from '@pets-ui/Alert/Alert'
import { Box } from '@pets-ui/Box/Box'
import { Container } from '@pets-ui/Container/Container'
import { type MobileMediaQuery } from '@pets-ui/tokens/breakpoints.stylex'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { VStack } from '@pets-ui/VStack/VStack'
import * as stylex from '@stylexjs/stylex'
import { type AxiosError } from 'axios'
import { useEffect, useRef, useState } from 'react'
import { FormProvider, useForm, useFormContext } from 'react-hook-form'
import { useLocation, useParams } from 'react-router-dom-v5'
import { PaymentProvider, useContextPayment } from '../../fp-b2b-shared/components/PaymentProvider/PaymentProvider'
import { CKO_ERROR_MESSAGE } from '../../fp-b2b-shared/constants/cko-error-message'
import { useCheckout } from '../../fp-b2b-shared/hooks/use-checkout'
import { useFpB2bPaymentDispatch } from '../../fp-b2b-shared/hooks/use-fp-b2b-payment-dispatch'
import { getErrorContent } from '../../fp-b2b-shared/utils/get-error-content'
import { CartFooterDetail } from '../components/PageLayout/CartFooterDetail'
import { PageFooter } from '../components/PageLayout/PageFooter'
import { getDefaultPaymentMethod } from './Checkout'
import { DeliveryNotes } from './components/DeliveryNotes/DeliveryNotes'
import { DIALOG_CHECKOUT_NOTIFY, DialogCheckoutNotify } from './components/DialogCheckoutError/DialogCheckoutNotify'
import { InvoiceCategory } from './components/InvoiceCategory/InvoiceCategory'
import { MerchantInfo } from './components/MerchantInfo/MerchantInfo'
import { OrderNotes } from './components/OrderNotes/OrderNotes'
import { PaymentMethod as MerchantPaymentMethod } from './components/PaymentMethod/PaymentMethod'
import { RetryPaymentDetailsUI } from './components/RetryPayment/RetryPaymentDetailsUI'
import { RetryPaymentFooterAction } from './components/RetryPaymentFooterAction'
import { ShippingMethod } from './components/ShippingMethod/ShippingMethod'
import { StmAlipayDialog } from './StmAlipayDialog'
import { StmApplePayDialog } from './StmApplePayDialog'
import { StmCreditCardDialog } from './StmCreditCardDialog'

const MOBILE: MobileMediaQuery = '@media (max-width: 480px)'

const styles = stylex.create({
  formRoot: {
    backgroundColor: colors.grey100,
    minHeight: '100vh',
  },
  root: {
    padding: 20,
    gap: 20,
    backgroundColor: colors.grey100,
    display: {
      [MOBILE]: 'block',
      default: 'grid',
    },
    gridTemplateColumns: '2fr 1fr',
    paddingBottom: 80,
  },
})

export function RetryPayment() {
  const isFormInit = useRef(false)
  const { basePath, campaignCode, campaignId, countryCode, platform } = useCheckout()

  const location = useLocation()
  const { configuredPaymentModule, userPaymentTokenInfo, selectedPayment, setIsProcessingCheckout, isCurrentCreditCardRemembered } = useContextPayment()
  const { transactionId } = useParams<{ transactionId?: string }>()
  const { destructuredData: userInfo } = useApiCampaignMember({ code: campaignCode })
  const { destructuredData: ordersDetail, isLoading: isOrderDetailLoading } = useApiGetMemberPaymentTransactionRequest({ id: transactionId || '' })
  const [isProcessing, setIsProcessing] = useState(false)
  const isPaymentFailed = location.pathname.includes('payment-failed')

  const { reset, watch } = useFormContext<CheckoutOrder>()
  const { trigger: getPaymentTransaction } = useApiGetPaymentTransactionMutation()
  const { openDialog: openDialogCheckoutError } = useDialog(DIALOG_CHECKOUT_NOTIFY)
  const formValues = watch()
  const { paymentTransactionInfo, isLoading: isLoadingPaymentTransaction } = useFpB2bCalculateResultWithTransactionInfo(campaignId, campaignCode, platform, transactionId)
  const deliveryClassList = useFpB2bDeliveryClassList(campaignId, platform)
  const deliveryClass = deliveryClassList[0] || DELIVERY_CLASS.NORMAL
  const { payMePaymentQRCodeUrl, setPayMePaymentQRCodeUrl } = useFpB2bPaymentService({ campaignId, paymentTransactionIdFromUrl: undefined, campaignCode, platform })

  // CKO error kind → 具體文案；否則攤平 BE detail / message，最後才泛用 fallback（對齊 Checkout）
  const showPaymentError = useCallbackEvent((error: unknown) => {
    const ckoErrorInfo = getCkoPaymentErrorInfo(error)
    if (ckoErrorInfo) {
      openDialogCheckoutError({ title: '付款錯誤', content: CKO_ERROR_MESSAGE[ckoErrorInfo.kind] })
      return
    }
    const errorData = (error as AxiosError<{ error: { detail?: Record<string, string[]>[] | string[]; message?: string } }>).response?.data.error
    const content = errorData?.detail ? getErrorContent(errorData.detail) : errorData?.message ?? '付款失敗，請稍後再試或選擇其他付款方式。'
    openDialogCheckoutError({ title: '付款錯誤', content })
  })

  const buildSuccessUrl = (paymentTransactionId: string) => `${window.location.origin}${basePath}/thankyou/${paymentTransactionId}`
  const buildFallbackUrl = () => `${window.location.origin}${basePath}/retry-payment/${transactionId}`

  const handlePaymentPrimeError = useCallbackEvent((error: unknown) => {
    if (error instanceof PaymentError) {
      datadogRum.addError(error, {
        function: '[getPaymentModulePrime] Payment module error',
        service: error.service,
        code: error.code,
        detail: error.detail,
      })
    }
    else {
      datadogRum.addError(error, {
        function: '[getPaymentModulePrime] Unexpected error',
      })
    }
    showPaymentError(error)
  })

  const {
    dispatch,
    applePayDialogContext,
    closeApplePayDialog,
    alipayDialogContext,
    closeAlipayDialog,
    creditCardDialogContext,
    closeCreditCardDialog,
  } = useFpB2bPaymentDispatch({
    paymentModule: configuredPaymentModule,
    urlBuilders: { buildSuccessUrl, buildFallbackUrl },
    onPaymentPrimeError: handlePaymentPrimeError,
  })

  useEffect(() => {
    /**
     * Only stay in the page when the payment is pending
     */
    if (isLoadingPaymentTransaction) return
    if (paymentTransactionInfo?.status && paymentTransactionInfo?.status === FastpassOrderStatus.PENDING) return
    window.location.href = `${window.location.origin}${basePath}/product-catalog`
  }, [basePath, isLoadingPaymentTransaction, paymentTransactionInfo, paymentTransactionInfo?.status])

  const processCheckout = async (): Promise<string | undefined> => {
    try {
      if (!transactionId) {
        return undefined
      }
      const transactionResponse = await getPaymentTransaction({ id: transactionId })

      if (!campaignCode || !transactionId) {
        throw new Error('Missing code, total, transactionId')
      }

      const { id: paymentTransactionId, order_hash: orderHash } = transactionResponse.data.data
      // CKO 彈窗路線（信用卡 / AliPayHK）dispatch 回 undefined、交棒給 dialog；此處不需再處理 redirect
      const paymentResult = await dispatch({
        paymentTransactionId: String(paymentTransactionId),
        paymentOptionId: selectedPayment?.id,
        orderHash: String(orderHash),
        email: formValues.billing_address_email,
        bankCode: formValues.bank_code,
        userPaymentTokenInfo,
        rememberPaymentCardToken: isCurrentCreditCardRemembered,
      })
      if (paymentResult && 'redirect_url' in paymentResult) {
        if (isPayMeUrl(paymentResult.redirect_url)) {
          setPayMePaymentQRCodeUrl(paymentResult.redirect_url)
          return paymentResult.redirect_url
        }
        window.location.href = paymentResult.redirect_url
      }
      return undefined
    }
    catch (error) {
      console.error(`[Payment Retry] ${error}`)
      datadogRum.addError(error, {
        function: 'processCheckout',
        userInfo,
      })
      showPaymentError(error)
      return undefined
    }
  }

  const handleSubmit = () => {
    setIsProcessing(true)
    setIsProcessingCheckout(true)
    processCheckout().finally(() => {
      setIsProcessing(false)
      setIsProcessingCheckout(false)
    })
  }

  useEffect(() => {
    if (isOrderDetailLoading || isFormInit.current) {
      return
    }
    reset({
      billing_address_address1: ordersDetail?.shipping_address_1 || '',
      billing_address_email: ordersDetail?.receiver_email || '',
      billing_address_first_name: ordersDetail?.receiver_name || '',
      billing_address_phone: ordersDetail?.receiver_phone || '',
      charity_organization: '',
      customer_note: ordersDetail?.customer_note || '',
      delivery_date: ordersDetail?.delivery_date || '',
      delivery_time_slot: ordersDetail?.delivery_time_slot,
      need_order_pdf: !!ordersDetail?.need_pdf,
      posting_date_in_next_month: ordersDetail?.posting_date_in_next_month,
      receipt_type: ordersDetail?.invoice_option?.type as unknown as Invoice,
      refund_statement: ordersDetail?.refund_statement,
      business_ubn: ordersDetail?.invoice_option?.business_ubn || '',
      business_ubn_title: ordersDetail?.invoice_option?.business_ubn_title || '',
    })
    isFormInit.current = true
  }, [isOrderDetailLoading, ordersDetail, reset])

  const { startPolling, stopPolling } = useFpB2bPaymentTransactionPolling({
    paymentTransactionId: transactionId,
    basePath: basePath ?? '',
  })
  useEffect(() => {
    /** Poll for transaction state of QR code payment */
    if (!payMePaymentQRCodeUrl) return
    startPolling()
    return () => stopPolling()
  }, [payMePaymentQRCodeUrl, startPolling, stopPolling])

  if (isOrderDetailLoading || isLoadingPaymentTransaction) return <LoaderFullPage />

  return (
    <>
      <Box rootStyle={styles.formRoot}>
        {
          isPaymentFailed
          && <Container><Alert type="error">付款失敗，請重新付款</Alert></Container>
        }
        <Container rootStyle={styles.root}>
          <VStack gap={5}>
            <MerchantInfo />
            <ShippingMethod deliveryClass={deliveryClass} />
            <MerchantPaymentMethod />
            <InvoiceCategory />
            <DeliveryNotes />
            <OrderNotes />
          </VStack>
          <RetryPaymentDetailsUI />
        </Container>
        <PageFooter detail={<CartFooterDetail />} actionButton={<RetryPaymentFooterAction onSubmit={handleSubmit} isLoading={isProcessing} />} />
        <DialogCheckoutNotify />
      </Box>
      {!!payMePaymentQRCodeUrl && (
        <DialogPayMeQRCode
          url={payMePaymentQRCodeUrl}
          onRefresh={processCheckout}
        />
      )}
      {applePayDialogContext && configuredPaymentModule instanceof CheckoutPayApplePayModule && (
        <StmApplePayDialog
          context={applePayDialogContext}
          paymentModule={configuredPaymentModule}
          countryCode={countryCode}
          onClose={closeApplePayDialog}
          onError={(error) => {
            closeApplePayDialog()
            showPaymentError(error)
          }}
        />
      )}
      {creditCardDialogContext && configuredPaymentModule instanceof CheckoutPayCreditCardModule && (
        <StmCreditCardDialog
          context={creditCardDialogContext}
          paymentModule={configuredPaymentModule}
          countryCode={countryCode}
          onClose={closeCreditCardDialog}
          onError={(error) => {
            closeCreditCardDialog()
            showPaymentError(error)
          }}
        />
      )}
      {alipayDialogContext && configuredPaymentModule instanceof CheckoutPayAlipayHkModule && (
        <StmAlipayDialog
          context={alipayDialogContext}
          paymentModule={configuredPaymentModule}
          countryCode={countryCode}
          onErrorClose={() => {
            closeAlipayDialog()
            openDialogCheckoutError({ title: '付款錯誤', content: '付款發生問題，請稍後再試或選擇其他付款方式。' })
          }}
        />
      )}
    </>
  )
}

export function RetryPaymentProcessor() {
  const { campaignId, campaignCode, countryCode, platform } = useCheckout()
  const { transactionId } = useParams<{ transactionId?: string }>()
  const { destructuredData: transaction } = useApiPaymentTransaction(countryCode
    ? {
      country_code: countryCode,
      payment_transaction_id: transactionId ?? '',
    }
    : undefined)

  const { destructuredData: userInfo } = useApiCampaignMember({ code: campaignCode })
  const { merchant } = useFpB2bMerchantService(campaignId, platform)
  const isShopManager = userInfo?.roles.includes('shop_manager')
  const { destructuredData: merchantProfile, isLoading: isLoadingMerchantProfile } = useApiMerchantProfile(isShopManager ? { code: campaignCode, merchant_id: merchant?.index } : undefined)
  const { defaultCampaignPayment } = useFpB2bCartCalculate(campaignId, campaignCode, platform)

  const mappedDefaultPayment = getDefaultPaymentMethod(transaction?.payment_methods || [], defaultCampaignPayment, merchantProfile?.payment_method)

  const form = useForm<CheckoutOrder>({
    defaultValues: {},
    mode: 'onChange',
  })
  if (isLoadingMerchantProfile) return <LoaderFullPage />
  return (
    <PaymentProvider paymentMethods={transaction?.payment_methods} defaultPaymentMethod={mappedDefaultPayment || undefined}>
      <FormProvider {...form}>
        <form>
          <RetryPayment />
        </form>
      </FormProvider>
    </PaymentProvider>
  )
}
